import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { LOCAL_STOREGE_KEYS } from "../utils/constants";
import { setTokenInLocalStorage, refreshAuthToken, clearLocalStorageToken } from "../utils/helpers";

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export const axiosClient = axios.create({
  baseURL: baseUrl,
  headers: { "content-type": "application/json" }
});

axiosClient.interceptors.request.use(config => {
  config.headers.Authorization =
    config.headers["Authorization"] ?? "Bearer " + localStorage.getItem(LOCAL_STOREGE_KEYS.ACCESS_TOKEN);
  return config;
});

axiosClient.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger

    // If token was refreshed set new token in local storage
    if (response.data.session) {
      const {
        access_token: accessToken,
        expires_in: expiresIn,
        refresh_expires_in: refreshExpiresIn
      } = response.data.session;
      setTokenInLocalStorage(accessToken, expiresIn, refreshExpiresIn);
      setAuthHeaders();
    }
    return response;
  },
  async function (error: AxiosError) {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url: string = (original?.url || (error.request && error.request.responseURL) || "") as string;
    // Logowanie, 2FA, wylogowanie i refresh same obsługują 401 — nie odświeżaj ani nie przekierowuj.
    const isAuthFlow =
      url.includes("/login") || url.includes("/two_factor/") || url.includes("/refresh_token") || url.includes("/oidc/");

    // 401 na uwierzytelnionym endpoincie: nasz krótkożyciowy JWT (życie = keycloak access token, ~5 min)
    // wygasł w trakcie sesji. Odśwież JEDNOKROTNIE (single-flight) i powtórz żądanie. Refresh-token
    // wciąż ważny → cicha kontynuacja; nieważny/odrzucony przez Keycloak → sesja martwa, na logowanie.
    if (status === 401 && !isAuthFlow && original && !original._retry) {
      const refreshExpiry = Number(localStorage.getItem(LOCAL_STOREGE_KEYS.REFRESH_TOKEN_EXPITY_DATE));
      if (refreshExpiry && refreshExpiry > Date.now()) {
        original._retry = true;
        const newToken = await refreshAuthToken();
        if (newToken) {
          original.headers.Authorization = "Bearer " + newToken;
          return axiosClient(original);
        }
      }
      // Brak ważnego refresh tokenu albo Keycloak odrzucił odświeżenie → realny koniec sesji.
      clearLocalStorageToken();
      const path = window.location.pathname;
      // Tylko strony chronione (panel) wyrzucają na logowanie. Strony publiczne (głosowanie, profil,
      // zapisy, aktywne głosowania, strona główna) NIE przekierowują ani nie przeładowują — inaczej
      // martwy/wygasły token tworzy pętlę (publiczne 401 → reload → 401…). Token już wyczyszczony,
      // widok dalej działa jako wylogowany.
      if (path.startsWith("/organizator") && !path.startsWith("/logowanie")) {
        window.location.href = "/logowanie";
      }
      return Promise.reject(error);
    }

    if (!isAuthFlow && status === 404 && window.location.pathname !== "/nie-znaleziono-strony") {
      window.location.href = "/nie-znaleziono-strony";
    }

    // Brak odpowiedzi = serwer nieosiągalny (down / sieć / timeout), a nie 4xx/5xx. Zamiast crasha
    // („undefined is not an object (response.status)") → dedykowany widok /blad-serwera.
    // Opt-out (skipServerErrorRedirect) dla wywołań tła, które nie powinny wywalić całej strony.
    const noResponse = !error.response && error.code !== "ERR_CANCELED";
    const skipServerError = Boolean((original as { skipServerErrorRedirect?: boolean } | undefined)?.skipServerErrorRedirect);
    if (noResponse && !isAuthFlow && !skipServerError && window.location.pathname !== "/blad-serwera") {
      window.location.href = "/blad-serwera";
    }
    return Promise.reject(error);
  }
);

export const setAuthHeaders = () => {
  axiosClient.defaults.headers.common["Authorization"] =
    "Bearer " + localStorage.getItem(LOCAL_STOREGE_KEYS.ACCESS_TOKEN);
};
